import ApiCollection             from '@widesk-core/models/ApiCollection';
import PagedCollection           from '@widesk-core/models/PagedCollection';
import PlaceModel                from '@models/map/PlaceModel';
import PlaceTypeModel            from '@models/map/PlaceTypeModel';
import ProjectJsonModel          from '@models/map/ProjectJsonModel';
import ProjectVersionModel       from '@models/map/ProjectVersionModel';
import StonecodeFileFileModel    from '@models/file/StonecodeFileFileModel';
import { reachableDocumentIds }  from '@tokamap/core';
import { Shape }                 from '@tokamap/core';
import { ShapeThemes }           from '@tokamap/core';
import { parseProjectThemes }    from '@tokamap/core';
import { pairMapsWithDocuments } from '@/TokamapEditor/stores/pairMapsWithDocuments';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import { makeAutoObservable }    from 'mobx';
import { runInAction }           from 'mobx';
import { getIdFromUrn }          from '@widesk-core/tools/identifier';

/** Une carte du projet telle que l'aperçu la manipule : son JSON, plus de quoi la désigner. */
export interface PreviewMap {
	mapId: id;
	label: string;
	json: unknown;
	/**
	 * Formes du document, analysées à l'avance — ce qui rend une carte cherchable sans être affichée.
	 * Les `Shape.id` qu'on y lit ne servent qu'à compter/parcourir : une forme dont le JSON ne porte
	 * pas d'identifiant en reçoit un nouveau à chaque analyse, donc ils ne désignent pas les mêmes
	 * formes que celles chargées dans la carte (voir `PreviewModal`, qui retrouve une forme par son
	 * point d'intérêt, jamais par cet identifiant).
	 */
	shapes: Shape[];
	/**
	 * Géolocalisation activée sur cette carte. C'est ce qui distingue une carte où l'on se repère
	 * d'une carte qu'on ne fait que consulter : ses points d'intérêt ne sont pas une destination
	 * d'itinéraire tant qu'elle est désactivée.
	 */
	geolocation: boolean;
}

export interface PreviewPlaceType {
	id: string;
	label: string;
	color?: string;
}

/**
 * Toutes les cartes du projet chargées pour une ouverture de l'aperçu, et celle qui est affichée.
 * Construit une fois à l'ouverture (`load`), jamais rechargé : l'aperçu est un instantané.
 */
export default class PreviewMapsStore {
	maps: PreviewMap[] = [];

	currentMapId?: id;

	placeTypes: PreviewPlaceType[] = [];

	placeTypeIdByPlaceId: Record<string, string> = {};

	constructor() {
		makeAutoObservable(this);
	}

	/**
	 * Charge les documents de toutes les cartes du projet, en une requête. Deux sources, le même
	 * format `{ maps, configuration }` : le projet tel qu'il est aujourd'hui, ou tel qu'il était en
	 * ligne à la publication passée en `version`. Ni l'une ni l'autre ne nomme ses cartes, d'où
	 * l'appariement positionnel de `pairMapsWithDocuments`.
	 */
	async load(currentMapId?: id, version?: ProjectVersionModel) {
		let documents: ProjectDocuments = { maps: [], themes: {} };

		try {
			documents = version ? await loadPublishedDocuments(version) : await loadCurrentDocuments();
		} catch (err) {
			console.error('Aperçu : chargement des cartes du projet', err);
		}

		const previewMaps = pairMapsWithDocuments(
			currentProjectEditorStore.get.mapCollection.models,
			documents.maps,
			documents.themes,
		);

		const { placeTypes, placeTypeIdByPlaceId } = await loadPlaceTypes(previewMaps);

		runInAction(() => {
			this.placeTypes = placeTypes;
			this.placeTypeIdByPlaceId = placeTypeIdByPlaceId;
			this.maps = previewMaps;
			// La carte ouverte dans l'éditeur si elle a bien été chargée, la première sinon.
			this.currentMapId = previewMaps.find(map => map.mapId === currentMapId)?.mapId
				?? previewMaps[0]?.mapId;
		});
	}

	get currentMap(): PreviewMap | undefined {
		return this.maps.find(map => map.mapId === this.currentMapId);
	}

	/** JSON de la carte affichée — un document vide tant qu'aucune carte n'a pu être chargée. */
	get currentJson(): unknown {
		return this.currentMap?.json ?? {};
	}

	placeTypeIdOf(placeId: number | string | undefined): string | undefined {
		return placeId === undefined ? undefined : this.placeTypeIdByPlaceId[String(placeId)];
	}

	setCurrentMapId(mapId: id) {
		this.currentMapId = mapId;
	}

	/** Les cartes du projet autres que celle affichée — celles dans lesquelles il reste à chercher. */
	get otherMaps(): PreviewMap[] {
		return this.maps.filter(map => map.mapId !== this.currentMapId);
	}

	/**
	 * Les cartes qu'un itinéraire peut relier à celle affichée — celles, et uniquement celles, dont
	 * les points d'intérêt ont un sens comme départ ou comme arrivée.
	 *
	 * Deux conditions, pour deux raisons distinctes : la géolocalisation, parce qu'on ne guide pas
	 * quelqu'un sur une carte où l'on ne sait pas le situer ; et la liaison, parce qu'il faut pouvoir
	 * aller sur la carte ET en revenir — c'est la zone de retour qui indique au calcul où l'on
	 * ressort (voir `reachableDocumentIds`, qui porte la règle au plus près du calcul qui doit s'y
	 * conformer). Les cartes s'enchaînent, donc une carte reliée de proche en proche compte aussi.
	 */
	get otherRoutableMaps(): PreviewMap[] {
		const reachable = reachableDocumentIds(
			this.maps.map(map => ({ documentId: map.mapId, shapes: map.shapes })),
			this.currentMapId ?? '',
		);

		return this.otherMaps.filter(map => map.geolocation && reachable.has(map.mapId));
	}

	/** Vrai dès que le projet a plus d'une carte, donc qu'un résultat a besoin de dire de laquelle il vient. */
	get hasSeveralMaps(): boolean {
		return this.maps.length > 1;
	}
}

type EmbeddedPlace = {
	id?: number | string;
	placeType?: { urn?: Urn; label?: string; color?: string } | null;
};

function placeOfShape(shape: Shape): EmbeddedPlace | undefined {
	return (shape.entity as { entity?: EmbeddedPlace } | undefined)?.entity;
}

export async function loadPlaceTypes(maps: PreviewMap[]) {
	const placeTypesById = new Map<string, PreviewPlaceType>();
	const placeTypeIdByPlaceId: Record<string, string> = {};
	const placeIdsWithoutType = new Set<string>();

	maps.flatMap(map => map.shapes).forEach(shape => {
		const place = placeOfShape(shape);

		if (place?.id === undefined || place.id === null) return;

		const placeId = String(place.id);
		const placeType = place.placeType;

		if (!placeType?.urn) {
			if (placeType === undefined) placeIdsWithoutType.add(placeId);

			return;
		}

		const id = String(getIdFromUrn(placeType.urn));

		placeTypeIdByPlaceId[placeId] = id;
		placeTypesById.set(id, { id, label: placeType.label || '', color: placeType.color || undefined });
	});

	Object.keys(placeTypeIdByPlaceId).forEach(placeId => placeIdsWithoutType.delete(placeId));

	if (placeIdsWithoutType.size) {
		try {
			const places = new ApiCollection(PlaceModel);

			await places.listBy([...placeIdsWithoutType], 'id');

			places.models.forEach(place => {
				const placeTypeId = place.getId('placeType');

				if (placeTypeId) placeTypeIdByPlaceId[String(place.id)] = String(placeTypeId);
			});

			const missingTypeIds = [...new Set(Object.values(placeTypeIdByPlaceId))].filter(id => !placeTypesById.has(id));
			const types = new ApiCollection(PlaceTypeModel);

			if (missingTypeIds.length) await types.listBy(missingTypeIds, 'id');

			types.models.forEach(type => placeTypesById.set(String(type.id), {
				id: String(type.id),
				label: type.label,
				color: type.color || undefined,
			}));
		} catch (err) {
			console.error('Aperçu : chargement des types de points d\'intérêts', err);
		}
	}

	const placeTypes = [...placeTypesById.values()].sort((a, b) => a.label.localeCompare(b.label));

	return { placeTypes, placeTypeIdByPlaceId };
}

/** Les thèmes sont à la RACINE de la réponse, pas dans le document d'une carte : ils appartiennent au
 * projet, et `pairMapsWithDocuments` les y redescend. */
export interface ProjectDocuments {
	maps: unknown[];
	themes: ShapeThemes;
}

/**
 * Les documents du projet tel qu'il est aujourd'hui.
 *
 * Exportée : `checkMapLinks` repart des mêmes documents.
 */
export async function loadCurrentDocuments(): Promise<ProjectDocuments> {
	const projectJson = new ProjectJsonModel({ id: currentProjectEditorStore.get.project.id });

	await projectJson.fetch();

	const root = projectJson.json as { maps?: unknown[]; themes?: unknown };

	return { maps: root?.maps ?? [], themes: parseProjectThemes(root?.themes) };
}

/**
 * Les documents du projet tel qu'il était EN LIGNE à cette publication, lus dans son fichier
 * condensé.
 *
 * Ce fichier DEVRAIT porter toutes les cartes du projet — celles que la publication a mises à jour
 * comme les autres, chacune dans la dernière version où elle avait été publiée. Aujourd'hui il ne
 * porte que les cartes mises à jour : une publication partielle donne donc moins de documents que le
 * projet n'a de cartes, et `load` refuse alors d'apparier. À corriger à l'assemblage, côté API : le
 * même fichier sert la route publique du projet, et le front ne peut rien pour ce que voient les
 * visiteurs.
 *
 * Le fichier est retrouvé par l'entité à laquelle il est rattaché plutôt que par `ProjectVersion.fileUrl` :
 * c'est déjà comme ça que l'aperçu retrouvait le compilé d'une version de carte, et une publication
 * n'a qu'un fichier.
 *
 * Exportée : `restoreProjectVersion` repart du même fichier.
 */
export async function loadPublishedDocuments(version: ProjectVersionModel): Promise<ProjectDocuments> {
	const files = new PagedCollection(StonecodeFileFileModel);

	await files
		.setItemsPerPage(1)
		.setSort('id', 'desc')
		.setRequiredFilter('typeSource.source.entityUrn', version.urn)
		.list();

	const downloadPath = files.first()?.downloadPath;

	if (!downloadPath) throw new Error(`Publication ${version.number} : fichier condensé introuvable.`);

	const response = await fetch(downloadPath);

	const root = (await response.json()) as { maps?: unknown[]; themes?: unknown };

	return { maps: root?.maps ?? [], themes: parseProjectThemes(root?.themes) };
}
